import * as React from "react";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext, ThemeContext } from "../context";
import { getConfig, titlePage } from "../utilities";
import { sendNotification } from "../hooks/useNotification";
import { NotificationContent } from "../components";
import {
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogContent,
  DialogTitle,
  Button
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

export default function LoginTransition() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configUser, setConfigUser] = useState({});
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const processedRef = useRef(false);

  const {
    state: { titoloPrivacyDialog, corpoPrivacyDialog },
    loginFromSessionData,
    postAccettaPrivacy,
    logout
  } = React.useContext(AuthContext);

  const {
    state: { theme },
  } = React.useContext(ThemeContext);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    processLoginData();
  }, []);

  const processLoginData = async () => {
    try {
      // 1. Leggere dati da query parameters (codificati in Base64)
      let loginDataRaw = null;
      const dataParam = searchParams.get('data');

      if (dataParam) {
        try {
          // Decodifica da Base64
          loginDataRaw = atob(dataParam);
          // Pulisci l'URL dai parametri sensibili
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (decodeError) {
          throw new Error('Errore nella decodifica dei dati');
        }
      }

      // Fallback: prova dal sessionStorage (stesso dominio)
      if (!loginDataRaw) {
        loginDataRaw = sessionStorage.getItem('loginData');
      }

      if (!loginDataRaw) {
        throw new Error('Nessun dato di login trovato. Accesso non autorizzato.');
      }

      // 2. Parsare JSON
      let loginData;
      try {
        loginData = JSON.parse(loginDataRaw);
      } catch (parseError) {
        throw new Error('Formato dati di login non valido');
      }

      // 3. Validare campi obbligatori
      if (!loginData.token) {
        throw new Error('Token mancante nei dati di login');
      }
      if (!loginData.configurazione) {
        throw new Error('Configurazione mancante nei dati di login');
      }
      if (!Array.isArray(loginData.configurazione)) {
        throw new Error('Il campo configurazione deve essere un array');
      }

      // 4. Recuperare configurazione tema
      const config = await getConfig(window.location.href);
      localStorage.setItem("configurazioni", JSON.stringify(config));
      titlePage(config.titleshort, "Login");
      setConfigUser(config);

      // 5. Pulizia sessionStorage
      sessionStorage.removeItem('loginData');

      // 6. Chiamare la funzione del context per completare il login
      await loginFromSessionData(loginData, navigate, config, setPrivacyDialogOpen);

      setIsLoading(false);

    } catch (err) {
      console.error('LoginTransition error:', err);
      setError(err.message);
      setIsLoading(false);

      sendNotification(
        <NotificationContent service={"Errore!"} />,
        err.message,
        "error",
        10
      );

      // Redirect alla login normale dopo errore
      setTimeout(() => {
        navigate("/");
      }, 3000);
    }
  };

  const handleAccettazionePrivacy = () => {
    postAccettaPrivacy(navigate, setPrivacyDialogOpen);
  };

  const handleRifiutoPrivacy = () => {
    setPrivacyDialogOpen(false);
    logout(navigate);
  };

  // UI di loading
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: theme ? 'background.paper' : 'background.default'
        }}
      >
        <CircularProgress color="primary" size={60} />
        <Typography sx={{ mt: 3 }} variant="h6">
          Autenticazione in corso...
        </Typography>
        <Typography sx={{ mt: 1 }} variant="body2" color="text.secondary">
          Attendere prego
        </Typography>
      </Box>
    );
  }

  // UI di errore
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: theme ? 'background.paper' : 'background.default'
        }}
      >
        <Typography color="error" variant="h5">
          Errore di autenticazione
        </Typography>
        <Typography sx={{ mt: 2, maxWidth: 400, textAlign: 'center' }}>
          {error}
        </Typography>
        <Typography sx={{ mt: 3 }} variant="body2" color="text.secondary">
          Reindirizzamento alla pagina di login...
        </Typography>
        <CircularProgress size={20} sx={{ mt: 2 }} />
      </Box>
    );
  }

  // Dialog Privacy
  return (
    <>
      <Dialog
        open={privacyDialogOpen}
        keepMounted
        onClose={() => { setPrivacyDialogOpen(false) }}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{titoloPrivacyDialog}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            <ReactMarkdown
              children={corpoPrivacyDialog}
              rehypePlugins={[rehypeRaw]}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRifiutoPrivacy}>
            Rifiuto
          </Button>
          <Button onClick={handleAccettazionePrivacy}>
            Accetto
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
